/**
 * First exposure: showing an item before it is ever asked for (TASKS T59c).
 *
 * ## Why this exists
 *
 * Raised from a real ronda: *"in the flashcard rondas I'm being asked to type in a word I've never
 * seen before. So either I just click 'I don't know' or google it."* Both of those are correct
 * behaviour on his part and a defect in the game.
 *
 * The evidence this whole app is built on is that **retrieval** beats restudy (RESEARCH §1), and
 * nothing here weakens that. But a retrieval attempt on an item that was never encoded is not a hard
 * retrieval, it is an impossible one: it teaches nothing, it costs the item an FSRS lapse it did not
 * earn, and — worse — it trains the learner that "no sé" is the normal answer. So a new item is now
 * *shown once* and then immediately *asked for*. The retrieval still happens; it happens against
 * something that is there to be retrieved.
 *
 * ## What this step is not
 *
 * - **Not scored.** No `Attempt`, no rating, no interval movement. It is not a question.
 * - **Not a scaffold.** It cannot be asked for. It is a property of the draw — the item has never
 *   been attempted — and the state machine has no edge into it except from `drawing`. A first
 *   exposure the learner could summon would be a free hint with no cost (invariant 2).
 * - **Not repeatable.** Once an item has been attempted at all, it is never introduced again, even
 *   if every attempt since has failed. The thing that responds to repeated failure is the encoding
 *   step (invariant 5), which is a different intervention for a different problem.
 *
 * Pure. No DOM, no clock, no store.
 */

import type { ItemRecord } from '../store/schema';
import { PERSON_LABEL, TENSE_LABEL } from './conjugate';
import type { ItemExtras } from './types';

/** One supporting line under the word: a carrier sentence, a rule, a slot. */
export interface TeachingLine {
  /** Spanish where the line is Spanish, so a screen reader and the stylesheet both know. */
  spanish: boolean;
  text: string;
  testId: string;
}

export interface TeachingView {
  /** What kind of thing this is. Never "new card" — the learner is not collecting anything. */
  label: string;
  /**
   * The Spanish to say aloud, and the sentence to say it in.
   *
   * Two, because they sound different and both are worth hearing. An isolated word gets a
   * synthesiser's list intonation — flat, clipped, and the thing people mean by "robotic". The same
   * word inside its carrier sentence gets real prosody, because the engine has somewhere to put the
   * stress and something to fall at the end of.
   *
   * `sentence` is null where the item has no carrier — a bare tier-2 word, or anything the corpus
   * gives no example for.
   */
  say: { word: string; sentence: string | null };
  /** The tier, so the card can carry its hue. Decorative (D-185). */
  tier: number;
  /** The Spanish, large. This is the thing being learned. */
  es: string;
  /** The English. */
  en: string;
  lines: TeachingLine[];
  announce: string;
}

const TIER_LABEL: Readonly<Record<number, string>> = {
  1: 'A new sound',
  2: 'A new word',
  3: 'A new verb form',
  4: 'A new rule',
  5: 'A new phrase',
  6: 'A new tense',
};

/**
 * Build the first-exposure card.
 *
 * Everything the corpus knows about the item goes on it — the carrier sentence, the conjugation
 * slot, the rule — because this is the one moment in the item's life where showing is the point.
 * Every other card in the game is careful to withhold most of this.
 */
export function teachingView(item: ItemRecord, extras: ItemExtras = {}): TeachingView {
  const lines: TeachingLine[] = [];

  // The sentence it lives in. For a single word this is most of what makes it usable.
  if (extras.example) {
    lines.push({ spanish: true, text: extras.example.es, testId: 'teach-example' });
    lines.push({ spanish: false, text: extras.example.en, testId: 'teach-example-en' });
  }

  // What the slot will ask for, in the same words the conjugation card uses, so the card he meets
  // ninety seconds from now reads as the same question rather than a new one.
  if (extras.slot) {
    lines.push({
      spanish: false,
      text: `From ${extras.slot.infinitive} — ${PERSON_LABEL[extras.slot.person]}, ${
        TENSE_LABEL[extras.slot.tense]
      }`,
      testId: 'teach-slot',
    });
  }

  // The rule, and — where this is a word the rule does not reach — the fact that it does not.
  if (extras.conversion) {
    lines.push({ spanish: false, text: extras.conversion.rule, testId: 'teach-rule' });
    if (extras.conversion.hazard) {
      lines.push({
        spanish: false,
        text: extras.conversion.hazard.note,
        testId: 'teach-hazard',
      });
    } else if (extras.conversion.gender) {
      lines.push({
        spanish: false,
        text: extras.conversion.gender === 'f' ? 'Always feminine.' : 'Always masculine.',
        testId: 'teach-gender',
      });
    }
  }

  /*
   * The derivation, last, because it is the line worth leaving the card with.
   *
   * Everything above says what this item *is*. This says why it is that, in terms of something the
   * learner already has — `situation` ends `-tion`, so it ends `-ción`; `hablar` is an `-ar` verb,
   * so its subjunctive borrows the `-er` endings. It is the only line on any card in this game that
   * is meant to make the item itself unnecessary: a learner holding the derivation can regenerate
   * the word, and the several hundred like it that will never be on a card.
   */
  if (extras.derivation) {
    lines.push({ spanish: false, text: extras.derivation, testId: 'teach-derivation' });
  }

  const label = TIER_LABEL[item.tier] ?? 'Something new';
  return {
    label,
    tier: item.tier,
    es: item.es,
    en: item.en,
    say: { word: item.es, sentence: extras.example?.es ?? null },
    lines,
    // The Spanish first, then the English: a screen reader user gets the thing being learned before
    // the gloss, which is the order the card is read in too.
    announce: `${label}. ${item.es}. ${item.en}.`,
  };
}

/**
 * Has this item never been shown?
 *
 * One question, answered from one recorded fact. The introduction is **written down** when it
 * happens (`ItemRecord.introducedAt`) rather than inferred from the learner's history, and the
 * difference is not academic — inferring it was wrong in a way that only an established store
 * revealed.
 *
 * The old rule read "no attempt for this item, and its memory is still `new`". Both halves are true
 * of an item met *before the first-exposure step existed*, so every word in a save older than the
 * feature was permanently un-introduced. Reported from a real play session: a cloze asking for
 * `abajo` in "Te espero ___", with the word never once shown.
 *
 * An absent field means "not yet shown", which is precisely what an older record says about itself —
 * so there is no migration to write. See DECISIONS D-213.
 */
export function needsFirstExposure(record: ItemRecord): boolean {
  return record.introducedAt === undefined;
}

/** The record as it should be stored once the learner has read the introduction. */
export function withIntroduction(record: ItemRecord, now: number): ItemRecord {
  return { ...record, introducedAt: now };
}
